// ProjectIntake.js — New Project.
// Box 1: jurisdiction, inspections, firm and engineer(s). Box 2: Section 1 of the
// chosen jurisdiction's form. The engineer and firm picks are saved to each
// discipline's form doc, where Project Details can change them later.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import ProjectInfoForm from '../components/ProjectInfoForm';
import { disciplinePaths, rosterFromUser } from '../disciplines';
import { EMPTY_PROJECT, formToProject } from '../projectFields';
import Footer from '../components/Footer';
import '../App.css';

const ProjectIntake = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [form, setForm]       = useState({ ...EMPTY_PROJECT });
  const [roster, setRoster]   = useState({ engineers: [], firms: [] });
  const [signer, setSigner]   = useState({ firmId: '', electrical: '', structural: '' });

  useEffect(() => {
    getDoc(doc(db, 'users', currentUser.uid))
      .then(u => setRoster(rosterFromUser(u.exists() ? u.data() : {})))
      .catch(e => console.warn('Could not load engineers and firms:', e));
  }, [currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.county) { setError('Select the jurisdiction.'); return; }
    if (!form.disciplines.length) { setError('Select at least one inspection: Electrical or Structural.'); return; }
    if (!form.address) { setError('Enter the street address.'); return; }
    setError('');
    setLoading(true);
    try {
      const docRef = await addDoc(collection(db, 'projects'), {
        ...formToProject(form),
        ownerId:   currentUser.uid,
        members:   [currentUser.uid],
        createdBy: currentUser.uid,
        createdAt: serverTimestamp(),
        status:    'In Progress',
      });
      await Promise.all(form.disciplines.map(k =>
        setDoc(doc(db, 'projects', docRef.id, 'forms', k),
          { engineerId: signer[k] || '', firmId: signer.firmId || '' }, { merge: true })
      ));
      navigate(disciplinePaths(docRef.id, form.disciplines[0]).details);
    } catch (err) {
      console.error('Error creating project:', err);
      setError('Failed to create project. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div>
      <Sidebar />
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.titleRow}>
            <h2 style={styles.title}>New Project</h2>
            <div style={styles.titleButtons}>
              <button type="button" onClick={() => navigate('/dashboard')} style={styles.cancelButton}>Cancel</button>
              <button type="submit" form="intake-form" className="main-content-button" style={styles.submitButton} disabled={loading}>
                {loading ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </div>

          {error && <div style={styles.errorBox}>{error}</div>}

          <form id="intake-form" onSubmit={handleSubmit}>
            <ProjectInfoForm form={form} setForm={setForm} roster={roster} signer={signer} setSigner={setSigner} />
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

const styles = {
  container:    { marginLeft: '200px', padding: '2rem', paddingBottom: '5rem', display: 'flex', justifyContent: 'center' },
  card:         { backgroundColor: 'rgba(255,255,255,0.92)', borderRadius: '8px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', width: '100%', maxWidth: '900px' },
  titleRow:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' },
  title:        { margin: 0, color: '#112b49', fontSize: '1.5rem' },
  titleButtons: { display: 'flex', gap: '0.75rem', alignItems: 'center' },
  cancelButton: { padding: '0.5rem 1.25rem', backgroundColor: '#6b7280', color: 'white', border: 'none', borderRadius: '5px', fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'inherit' },
  submitButton: { padding: '0.5rem 1.25rem', fontSize: '0.95rem', cursor: 'pointer', width: 'auto', margin: 0 },
  errorBox:     { padding: '0.75rem 1rem', borderRadius: '6px', border: '1px solid #fecaca', backgroundColor: '#fef2f2', color: '#b91c1c', fontSize: '0.875rem', marginBottom: '1rem' },
};

export default ProjectIntake;
